import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ResetPasswordRequest } from '../../models/auth/reset-password-request.model';
import { AuthService } from '../../services/auth.service';
import { NavigationService } from '../../services/navigation.service';
import { UtilsService } from '../../services/utils.service';
import { AuthenticationUserCompany } from '../../models/auth/auth-response.model';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { ActivatedRoute } from '@angular/router';
import { debounce } from 'lodash';
import { TranslocoService } from '@jsverse/transloco';
import { Utils } from '../../services/utils';
import { PasswordPolicyResponse } from '../../models/auth/password-policy-response.model';
declare const Swal: any;

@Component({
    selector: 'reset-password',
    templateUrl: 'reset-password.component.html',
    styleUrls: ['reset-password.component.scss'],
    standalone: false
})
export class ResetPasswordComponent implements OnInit {

  @ViewChild('resetPasswordForm') resetPasswordForm: NgForm | undefined;

  possibleLogoUrls: string[] = [];
  availableCompanies: AuthenticationUserCompany[] = [];

  errorMessage: string | null = null;

  password: string = "";
  confirmedPassword: string = "";

  resetPasswordButtonText: string;

  spinnerVisible: boolean = false;

  userName: string = "";
  userCompanyGuid: string = "";
  userScope: string = null;

  passwordInputState = {
    id: 'password3',
    displayed: false,
  };
  confirmPasswordInputState = {
    id: 'confirmedPassword3',
    displayed: false,
  };

  protected passwordIsMinimum8Characters: boolean = false;
  protected passwordContainsDigit: boolean = false;
  protected passwordContainsUppercase: boolean = false;
  protected passwordContainsLowercase: boolean = false;
  protected passwordContainsSpecialCharacter: boolean = false;

  passwordPolicy: PasswordPolicyResponse;

  private _token: string = "";

  constructor(
    private readonly _navigationService: NavigationService,
    private readonly _authService: AuthService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _utilsService: UtilsService,
    private readonly _environment: EnvironmentService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _translocoService: TranslocoService,
  ) {
    this.possibleLogoUrls = this._navigationService.possibleLogoUrls;
    this.resetPasswordButtonText = this._translocoService.translate('auth.resetPasswordDialog.title');
  }

  ngOnInit() {
    this._token = this._utilsService.getUrlParameter("token");
    this.userName = this._utilsService.getUrlParameter("userName");
    this.userCompanyGuid = this._utilsService.getUrlParameter("userCompanyGuid");

    let userScopeFromPath = null;
    if (this._activatedRoute.routeConfig && this._activatedRoute.routeConfig.data && this._activatedRoute.routeConfig.data.scope) {
      userScopeFromPath = this._activatedRoute.routeConfig.data.scope;
    }
    this.userScope = this._environment.scope || userScopeFromPath;

    if (this.userCompanyGuid) {
      this.loadPasswordPolicy(this.userCompanyGuid);
    }

    if (this.userName && !this.userCompanyGuid) {
      this.getAvailableCompanies();
    }
  }

  onResetPasswordClicked = () => {
    this.resetPasswordForm?.form.markAllAsTouched();
    if (this.resetPasswordForm?.form.valid && this.isPasswordValid) {
      this.toggleProgress(true);
      const request = new ResetPasswordRequest(this.userName, this.password, this.confirmedPassword, this._token, this.userCompanyGuid);
      this._authService.resetPassword(request).subscribe(result => {
        Swal.fire(
          this._translocoService.translate('auth.resetPassword.successTitle'),
          this._translocoService.translate('auth.resetPassword.successMessage'),
          'success'
        ).then(() => {
          this._navigationService.navigateToPath("apply", true);
        });
      }, error => {
        this.errorMessage = this._errorMsg(error);
      }).add(() => {
        this.toggleProgress(false);
      });
    }
  }

  onCompanyChanged(): void {
    if (this.userCompanyGuid) {
      this.loadPasswordPolicy(this.userCompanyGuid);
    }
  }

  onPasswordChanged = () => {
    const minLength = this.passwordPolicy?.minimumPasswordLength || 8;
    this.passwordContainsDigit = Utils.containsDigit(this.password);
    this.passwordIsMinimum8Characters = this.password.trim().length >= minLength;
    this.passwordContainsUppercase = Utils.containsUppercase(this.password);
    this.passwordContainsLowercase = Utils.containsLowercase(this.password);
    this.passwordContainsSpecialCharacter = Utils.containsSpecialCharacter(
      this.password
    );
  };

  get isPasswordValid(): boolean {
    if (!this.password) return false;
    if (!this.passwordIsMinimum8Characters) return false;
    const p = this.passwordPolicy;
    if (!p) {
      return this.passwordContainsDigit && this.passwordContainsUppercase
        && this.passwordContainsLowercase && this.passwordContainsSpecialCharacter;
    }
    if (p.requireDigit && !this.passwordContainsDigit) return false;
    if (p.requireUppercase && !this.passwordContainsUppercase) return false;
    if (p.requireLowercase && !this.passwordContainsLowercase) return false;
    if (p.requireSpecialCharacter && !this.passwordContainsSpecialCharacter) return false;
    return true;
  }

  protected onPasswordStateClicked = (state: { id: string, displayed: boolean }) => {
    state.displayed = !state.displayed;
    const passwordElement = document.getElementById(state.id);
    passwordElement.setAttribute('type', state.displayed ? 'text' : 'password');
  }

  onEmailChangedDebounce = debounce(this.onEmailChanged, 300);

  private onEmailChanged() {
    if (!this.userName) {
      this.userCompanyGuid = null;
      return;
    }

    this.getAvailableCompanies();
  }

  private toggleProgress = (value: boolean) => {
    if (value) {
      this._spinner.show();
    } else {
      this._spinner.hide();
    }
    this.spinnerVisible = value;
    if (value) {
      this.resetPasswordButtonText = this._translocoService.translate('common.pleaseWait');
    } else {
      this.resetPasswordButtonText = this._translocoService.translate('auth.resetPasswordDialog.title');
    }
  }

  private loadPasswordPolicy(userCompanyGuid: string): void {
    this._authService.getPasswordPolicy(userCompanyGuid).subscribe({
      next: (policy) => {
        this.passwordPolicy = policy;
        this.onPasswordChanged();
      }
    });
  }

  private getAvailableCompanies = () => {
    this._spinner.show();
    this._authService.getAvailableCompanies(this.userName, this.userScope || 'AgentAndBorrower').subscribe({
      next: (response) => {
        this.availableCompanies = response || [];

        if (this.availableCompanies.length === 1) {
          this.userCompanyGuid = this.availableCompanies[0].userCompanyGuid;
          this.loadPasswordPolicy(this.userCompanyGuid);
        }
      },
      error: (error) => {
        this.errorMessage = error?.error?.detail || error?.error?.message || this._translocoService.translate('auth.resetPassword.companiesLookupError');
      }
    }).add(() => {
      this._spinner.hide();
    });
  }

  private _errorMsg(error): string {
    const errorBody = typeof error === 'string' ? error : (error?.error ?? error?.message ?? '');
    const errorText = typeof errorBody === 'string' ? errorBody : (errorBody?.message ?? '');

    if (errorText.indexOf("InvalidToken") > -1) {
      return this._translocoService.translate('auth.resetPassword.invalidToken');
    }

    if (errorText) {
      return errorText;
    }

    return this._translocoService.translate('auth.resetPassword.passwordRequirements');
  }
}
